import { Skeleton } from "@mui/material";
import {
  Box,
  Card,
  CardContent,
  Stack,
  Tooltip,
  Typography,
} from "../../../../../components";
import { formatPrice } from "../../../../../utils";

const CustomTooltip = ({ infoContent, children }) => {
  if (!infoContent) return children;

  return (
    <Tooltip
      title={
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: 2,
            paddingRight: 5,
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: "bold" }}>
            Variáveis
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontStyle: "italic", whiteSpace: "pre-line" }}
          >
            {infoContent.variaveis}
          </Typography>
          <Typography variant="body1" sx={{ mt: 1, fontWeight: "bold" }}>
            Cálculo de Referência
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontStyle: "italic",
              whiteSpace: "pre-line",
              backgroundColor: "#e5e5e5",
              borderRadius: 2,
              py: "4px",
              mt: "4px",
              paddingLeft: 1,
              paddingRight: 2,
              color: "black",
            }}
          >
            {infoContent.calculo}
          </Typography>
        </Box>
      }
      placement="bottom"
      slotProps={{
        popper: {
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, -6],
              },
            },
          ],
        },
        tooltip: {
          sx: {
            borderRadius: 4,
            p: 2,
            display: "flex",
            alignItems: "center",
            flexDirection: "row",
            gap: 2,
            boxShadow: 3,
            backgroundColor: "background.paper",
          },
        },
      }}
    >
      {children}
    </Tooltip>
  );
};

function CardInfo({ card }) {
  return (
    <CustomTooltip infoContent={card.tooltip}>
      <Box display="flex" alignItems="center" gap={1.5}>
        <Box
          sx={{
            width: 46,
            height: 46,
            borderRadius: 2,
            backgroundColor: `${card.color}18`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <i
            className={`ph-bold ${card.icon}`}
            style={{ color: card.color, fontSize: 22 }}
          />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Stack spacing={0.25}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                lineHeight: 1.1,
                color: "#1e293b",
              }}
            >
              {card.value}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontWeight: 500,
                display: "block",
              }}
            >
              {card.label}
            </Typography>
            {card.percentual && (
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontWeight: 500,
                  display: "block",
                }}
              >
                {card.percentual}
              </Typography>
            )}
          </Stack>
        </Box>
      </Box>
    </CustomTooltip>
  );
}

export function DreSummaryCards({ rows, loading }) {
  const receitaBruta = rows.find((r) => r.id === "receita-bruta-vendas");
  const deducoes = rows.find((r) => r.id === "deducoes-receita-bruta");
  const faturamento = rows.find((r) => r.id === "faturamento-liquido");
  const lucro = rows.find((r) => r.id === "lucro-liquido");
  const custosVariaveis = rows.find((r) => r.id === "custos-variaveis");
  const margemContribuicao = rows.find((r) => r.id === "margem-contribuicao");
  const custosFixos = rows.find((r) => r.id === "custos-fixos");

  const pontoEquilibrio =
    custosFixos?.total / (margemContribuicao?.total / receitaBruta?.total);
  const faturamentoLiquido = faturamento?.total ?? 0;
  const formatFatorFaturamentoLiq = (valor) => {
    const fator = faturamentoLiquido && (valor / faturamentoLiquido) * 100;
    return `${fator.toFixed(2)}%`;
  };

  const summaryCards = [
    {
      label: "RECEITA BRUTA (ANO)",
      value: formatPrice(receitaBruta?.total ?? 0),
      icon: "ph-bold ph-trend-up",
      color: "#2563eb",
    },
    {
      label: "DEDUÇÕES (ANO)",
      value: formatPrice(deducoes?.total ?? 0),
      icon: "ph-bold ph-trend-down",
      color: "#dc2626",
    },
    {
      label: "FATURAMENTO LÍQUIDO",
      value: formatPrice(faturamentoLiquido),
      icon: "ph-fill ph-receipt",
      color: "#1e293b",
    },
    {
      label: "DESPESAS VARIÁVEIS",
      value: formatPrice(custosVariaveis?.total ?? 0),
      icon: "ph-fill ph-receipt-x",
      color: "#e31b1b",
      percentual: formatFatorFaturamentoLiq(custosVariaveis?.total ?? 0),
    },
    {
      label: "LUCRO BRUTO",
      value: formatPrice(margemContribuicao?.total ?? 0),
      icon: "ph-fill ph-currency-circle-dollar",
      color: "#388e3c",
      percentual: formatFatorFaturamentoLiq(margemContribuicao?.total ?? 0),
      tooltip: {
        variaveis: "Faturamento Líquido (FL) \nDespesas Variáveis (DV)",
        calculo: "Lucro Bruto = FL - DV",
      },
    },
    {
      label: "DESPESAS FIXAS",
      value: formatPrice(custosFixos?.total ?? 0),
      icon: "ph-fill ph-minus-circle",
      color: "#e31b1b",
      percentual: formatFatorFaturamentoLiq(custosFixos?.total ?? 0),
    },
    {
      label: "LUCRO LÍQUIDO",
      value: formatPrice(lucro?.total ?? 0),
      icon: "ph-bold ph-trend-up",
      color: (lucro?.total ?? 0) >= 0 ? "#16a34a" : "#dc2626",
      percentual: formatFatorFaturamentoLiq(lucro?.total ?? 0),
      tooltip: {
        variaveis: "Lucro Bruto (LB) \nDespesas Fixas (DF)",
        calculo: "Lucro Líquido = LB - DF",
      },
    },
    {
      label: "PONTO DE EQUILÍBRIO",
      value: formatPrice(pontoEquilibrio ?? 0),
      icon: "ph-bold ph-scales",
      color: (lucro?.total ?? 0) >= 0 ? "#16a34a" : "#dc2626",
      tooltip: {
        variaveis:
          "Lucro Bruto (LB) \nReceita Bruta (RB) \nDespesas Fixas (DF) \nFator de Equilíbrio (FB)",
        calculo:
          "Fator de Equilíbrio = LB / RB \nPonto de Equilíbrio = DF / FB",
      },
    },
  ];

  return (
    <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap={2} mb={3}>
      {summaryCards.map((card) => (
        <Card
          key={card.label}
          sx={{ borderRadius: 3, border: "1px solid #f0f0f0" }}
        >
          <CardContent
            sx={{
              p: 2,
              "&.MuiCardContent-root:last-child": {
                paddingBottom: "16px !important",
              },
            }}
          >
            {loading ? (
              <Box display="flex" alignItems="center" gap={1.5}>
                <Skeleton variant="rounded" width={46} height={46} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton width="60%" height={28} />
                  <Skeleton width="80%" height={16} />
                </Box>
              </Box>
            ) : (
              <CardInfo card={card} />
            )}
          </CardContent>
        </Card>
      ))}
    </Box>
  );
}

export function DreMargemCards({ rows, loading }) {
  const receitaBruta = rows.find((r) => r.id === "receita-bruta-vendas");
  const margem = rows.find((r) => r.id === "margem-contribuicao");
  const lucro = rows.find((r) => r.id === "lucro-liquido");

  const receitaTotal = receitaBruta?.total || 0;
  const margemPct = receitaTotal
    ? ((margem?.total ?? 0) / receitaTotal) * 100
    : 0;
  const lucroLiquidoPct = receitaTotal
    ? ((lucro?.total ?? 0) / receitaTotal) * 100
    : 0;

  const margemCards = [
    {
      label: "Margem de Contribuição",
      pct: margemPct,
      value: margem?.total ?? 0,
      color: "#1e293b",
    },
    {
      label: "Margem Líquida",
      pct: lucroLiquidoPct,
      value: lucro?.total ?? 0,
      color: lucroLiquidoPct >= 0 ? "#16a34a" : "#dc2626",
    },
  ];

  return (
    <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2} mb={3}>
      {margemCards.map((card) => (
        <Card
          key={card.label}
          sx={{ borderRadius: 3, border: "1px solid #f0f0f0" }}
        >
          <CardContent sx={{ p: 2 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: "#64748b", mb: 1 }}
            >
              {card.label}
            </Typography>
            {loading ? (
              <Skeleton width={100} height={40} />
            ) : (
              <Typography
                variant="h3"
                sx={{ fontWeight: 700, color: card.color, mb: 0.5 }}
              >
                {card.pct.toFixed(1)}%
              </Typography>
            )}
            <Typography variant="caption" sx={{ color: "#2563eb" }}>
              {formatPrice(card.value)}&nbsp;/&nbsp;receita bruta
            </Typography>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
}
